import { OrgResponse } from './../../../business/entities/organization/OrgResponse';
import { createContext, useContext } from 'react';
import { ProposalForm, ProposalBlockForm } from './ProposalForm';
import { ProposalBuilderPageBloc } from './ProposalBuilderPageBloc';
import { ProposalPreviewPageBloc } from '../ProposalPreviewPage/ProposalPreviewPageBloc';
import { UserResponse, CompactUserResponse } from '@business/entities/account';
import { ContactResponse } from '@business/entities/contact';
import { BrandResponse } from '@business/entities/brand';
import { CompanyResponse } from '@business/entities/companies';
import { ProposalPreviewPageBloc as ProposalPreviewPageBloc2 } from '@modules/proposals/ViewProposalModal/ProposalPreviewPage/ProposalPreviewPageBloc';
import { MultiplePreviewBlockBloc } from '../Blocks/BlockBase/MultiplePreviewBlock/MultiplePreviewBlockBloc';
import { ProposalStatus } from '@business/entities/proposal';
import { EstimateItemFormData } from '@modules/organizations/OrganizationPage/Components/Design/EstimateForm';
import { VeryCompactTMEstimateResponse } from '@business/entities/tm';
import { SubProjectFormData, SubProjectFormType } from '@modules/proposals/AddEditProposalPage/AddEditProposalForm';
// import { PackageFormData } from '@modules/proposals/AddEditProposalPage/AddEditProposalForm';

interface ProposalContextType {
  form: ProposalForm;
  currentSection: number;
  bloc: ProposalBuilderPageBloc | ProposalPreviewPageBloc | ProposalPreviewPageBloc2 | MultiplePreviewBlockBloc;
  scale: number;
  setScale: (scale: number) => void;
  sidebar: boolean;
  toggleSidebar: () => void;
  signee?: any;
  estimate?: EstimateItemFormData;
  // subProjects?: VeryCompactTMEstimateResponse[];
  subProjects?: SubProjectFormData[];

  // packages?: PackageFormData[];
  //   history: ProposalFormData[];
}

export const ProposalContext = createContext<ProposalContextType | null>(null);

export function useProposalContext() {
  return useContext(ProposalContext);
}

interface ProposalBlockContextType {
  blockForm: ProposalBlockForm;
  blockIndex: number;
}

export const ProposalBlockContext = createContext<ProposalBlockContextType | null>(null);

export function useProposalBlockContext() {
  return useContext(ProposalBlockContext);
}

export interface ProposalDataType {
  user?: UserResponse | null;
  organization?: OrgResponse | null;
  contact?: ContactResponse;
  company?: CompanyResponse;
  brand?: BrandResponse;
  dateSumitted: number;
  dateAccepted?: number;
  total: number;
  grandTotal?: number;
  signee?: any;
  senders?: any[];
  status?: ProposalStatus;
  startDate?: string;
  /** Derived: start date + weeks to complete (formatted) */
  endDate?: string;
  weeksToComplete?: number;
  proposalTitle?: string;
  proposalNumber?: string;
  /**
   * [templateId => hash] of the documents generated for this proposal's Terms & Conditions links
   * (tm_agreements rows, filled with this proposal's values). Absent while building a template.
   */
  documentLinks?: Record<string, string>;
  // Work-order people, used to resolve the project-list "Project Manager" / "Sales Person" short codes.
  projectManager?: CompactUserResponse | null;
  salesPersons?: CompactUserResponse[];
  // The Design File selected for this proposal — resolves template links and the
  // {{design_file_link}} / {{design_file_url}} short codes. Empty while building a template.
  designFileUrl?: string;
  designFileName?: string;
  // Past its expiry date: the customer can still read it but can no longer sign or pay
  isExpired?: boolean;
  expiresAt?: number | null;
}

export const ProposalDataContext = createContext<ProposalDataType | null>(null);

export function useProposalDataContext() {
  return useContext(ProposalDataContext);
}

/**
 * Collapse state of the proposal page tree (provided by `ProposalBlocs`). Collapsing a block hides all its
 * descendants; `BlockContainer` uses it for the section header chevron. Blocks rendered outside `ProposalBlocs`
 * (e.g. widget previews) get null and keep their own local state.
 */
export interface ProposalCollapseContextType {
  isCollapsed(blockIndex: number): boolean;
  toggle(blockIndex: number, collapsed?: boolean): void;
}

export const ProposalCollapseContext = createContext<ProposalCollapseContextType | null>(null);

export function useProposalCollapse() {
  return useContext(ProposalCollapseContext);
}
